import { Injectable, computed, inject, signal } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { AuthConfigService } from '../../../core/services/auth-config.service';
import { ZoneStateService } from '../../../core/services/zone-state.service';
import { AuthLoginPayload, AuthUser } from '../models/auth.models';
import { AuthApiService } from './auth-api.service';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  readonly #api = inject(AuthApiService);
  readonly #config = inject(AuthConfigService);
  readonly #zoneState = inject(ZoneStateService);

  readonly #accessToken = signal<string | null>(null);
  readonly #isBootstrapping = signal<boolean>(false);
  readonly #isInitialized = signal<boolean>(false);
  readonly #user = signal<AuthUser | null>(null);

  readonly accessToken = this.#accessToken.asReadonly();
  readonly isBootstrapping = this.#isBootstrapping.asReadonly();
  readonly isInitialized = this.#isInitialized.asReadonly();
  readonly user = this.#user.asReadonly();

  readonly displayName = computed(() => {
    const user = this.#user();

    if (!user) {
      return '';
    }

    return [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email;
  });
  readonly isAuthenticated = computed(() => {
    const user = this.#user();

    if (!user) {
      return false;
    }

    return this.#config.bypassLogin || !!this.#accessToken();
  });
  readonly roles = computed(() => this.#user()?.roles ?? []);
  readonly permissions = computed(() => this.#user()?.permissions ?? []);

  async bootstrap(): Promise<void> {
    this.#isBootstrapping.set(true);

    try {
      if (this.#config.requireLogin) {
        try {
          await this.refresh();
        } catch {
          this.clearSession();
        }

        return;
      }

      try {
        await this.loadCurrentUser();
      } catch {
        this.clearSession();
      }
    } finally {
      this.#isInitialized.set(true);
      this.#isBootstrapping.set(false);
    }
  }

  async login(payload: AuthLoginPayload): Promise<AuthUser> {
    const response = await firstValueFrom(this.#api.login(payload));
    this.#setSession(response.access_token, response.user);

    return response.user;
  }

  async refresh(): Promise<AuthUser> {
    const response = await firstValueFrom(this.#api.refresh());
    this.#setSession(response.access_token, response.user);

    return response.user;
  }

  async loadCurrentUser(): Promise<AuthUser> {
    const user = await firstValueFrom(this.#api.me());
    this.#user.set(user);
    this.#zoneState.setZones(user.zones ?? []);

    return user;
  }

  async logout(): Promise<void> {
    if (!this.#accessToken()) {
      this.clearSession();
      return;
    }

    try {
      await firstValueFrom(this.#api.logout());
    } finally {
      this.clearSession();
    }
  }

  hasRole(role: string): boolean {
    if (this.#config.bypassPermissions) {
      return true;
    }

    return this.roles().includes(role);
  }

  hasAnyRole(roles: string[]): boolean {
    if (this.#config.bypassPermissions) {
      return true;
    }

    if (!roles.length) {
      return true;
    }

    return roles.some((role) => this.roles().includes(role));
  }

  hasPermission(permission: string): boolean {
    if (this.#config.bypassPermissions) {
      return true;
    }

    return this.permissions().includes(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    if (this.#config.bypassPermissions) {
      return true;
    }

    if (!permissions.length) {
      return true;
    }

    return permissions.some((permission) => this.permissions().includes(permission));
  }

  clearSession(): void {
    this.#accessToken.set(null);
    this.#user.set(null);
    this.#zoneState.clear();
  }

  #setSession(accessToken: string, user: AuthUser): void {
    this.#accessToken.set(accessToken);
    this.#user.set(user);
    this.#zoneState.setZones(user.zones ?? []);
  }
}
